import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import PageHeader from './PageHeader';

import { API } from '../apiClient';
async function api(path, options = {}) {
  const token = localStorage.getItem('token');
  const headers = {
    ...(options.body && !(options.body instanceof FormData) ? {'Content-Type': 'application/json'} : {}),
    ...(options.headers || {}),
    ...(token ? {Authorization: `Bearer ${token}`} : {})
  };
  const res = await fetch(`${API}${path}`, {...options, headers});
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

function formatDateTime(value) {
  if (!value) return '—';
  return String(value).replace('T', ' ').slice(0, 16);
}

function channelLabel(channel) {
  if (channel === 'booking.com' || channel === 'booking') return 'Booking.com';
  if (channel === 'email') return 'Email';
  if (channel === 'manual') return 'Ręcznie';
  if (channel === 'hermes_gmail') return 'Hermes/Gmail';
  return channel || 'Nieznane';
}

function logTypeLabel(type) {
  if (type === 'run') return 'worker';
  if (type === 'booking') return 'rezerwacja';
  if (type === 'email') return 'email';
  if (type === 'message') return 'wiadomość';
  if (type === 'whatsapp') return 'whatsapp';
  return type || 'log';
}

function IntegrationLog({ title, items = [] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`integration-log ${open ? 'open' : ''}`}>
      <button type="button" className="integration-log-toggle" onClick={() => setOpen(v => !v)}>
        <span>{title}</span>
        <strong>{items.length} wpisów</strong>
        <em>{open ? 'Ukryj' : 'Pokaż'}</em>
      </button>
      {open && (items.length ? (
        <ul>
          {items.map(item => (
            <li key={item.id}>
              <div className="integration-log-top">
                <strong>{item.title}</strong>
                <time>{formatDateTime(item.at)}</time>
              </div>
              <p>{item.summary || 'Brak szczegółów.'}</p>
              {item.description && (
                <p className="integration-log-description"><strong>Opis:</strong> {item.description}</p>
              )}
              <div className="integration-log-meta">
                <span>{logTypeLabel(item.type)}</span>
                <span>{item.status || '—'}</span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="hint">Brak wpisów w logu.</p>
      ))}
    </div>
  );
}

export default function EmailImport() {
  const ctx = useOutletContext() || {};
  const bookings = ctx.bookings || [];
  const reload = ctx.reload || (async () => {});
  const [emailText, setEmailText] = useState('');
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [manualOpen, setManualOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState(null);
  const [statusErr, setStatusErr] = useState('');
  const [running, setRunning] = useState('');

  async function refreshIntegrations() {
    const [statusData, logsData] = await Promise.all([
      api('/integrations/status'),
      api('/integrations/logs')
    ]);
    setStatus(statusData);
    setLogs(logsData);
  }

  async function runNow(integration) {
    setRunning(integration);
    setStatusErr('');
    try {
      await api(`/integrations/run-now/${integration}`, { method: 'POST' });
      await refreshIntegrations();
    } catch (ex) {
      setStatusErr(ex.message || 'Nie udało się uruchomić integracji.');
    } finally {
      setRunning('');
    }
  }

  const imported = useMemo(() => bookings.filter(b => b.channel && b.channel !== 'manual'), [bookings]);
  const latestImported = useMemo(() => {
    return [...imported].sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))[0];
  }, [imported]);
  useEffect(() => {
    let mounted = true;
    refreshIntegrations()
      .catch(ex => { if (mounted) setStatusErr(ex.message || 'Nie udało się pobrać statusu integracji.'); });
    return () => { mounted = false; };
  }, []);

  const channels = useMemo(() => {
    const counts = {};
    for (const booking of bookings) {
      const key = channelLabel(booking.channel || 'manual');
      counts[key] = (counts[key] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [bookings]);

  async function submit(e) {
    e.preventDefault();
    setErr('');
    setResult(null);
    if (!emailText.trim()) {
      setErr('Wklej treść emaila przed importem.');
      return;
    }
    try {
      const res = await api('/import/email-text', {
        method: 'POST',
        body: JSON.stringify({ email_text: emailText, property_id: ctx.selectedApartmentId || null })
      });
      setResult(res);
      setEmailText('');
      await reload();
    } catch(ex) {
      setErr(ex.message || 'Nie udało się zaimportować emaila.');
    }
  }

  return (
    <section>
      <PageHeader
        icon="🔌"
        title="Integracje i import"
        description="To panel diagnostyczny. Głównym źródłem operacyjnym jest telefon / Booking Pulse; email działa wyłącznie jako awaryjny fallback."
        helpKey="integrations-import"
        stats={[
          { label: 'Rezerwacje razem', value: bookings.length, tone: 'info' },
          { label: 'Z importu', value: imported.length, tone: imported.length ? 'success' : 'warning' },
          { label: 'Ostatni import', value: latestImported ? formatDateTime(latestImported.created_at) : 'brak', tone: latestImported ? 'success' : 'warning' }
        ]}
      />


      {status?.source_priority?.length > 0 && (
        <article className="card integration-card integration-card-primary source-priority-card">
          <div className="integration-card-head">
            <span className="integration-icon">📡</span>
            <div>
              <h2>Priorytet źródeł</h2>
              <p className="hint">Telefon / Pulse ma najwyższy priorytet. Pozostałe kanały tylko go wspierają albo działają awaryjnie.</p>
            </div>
          </div>
          <div className="source-priority-list">
            {status.source_priority.map(item => (
              <div className="channel-row" key={item.key}>
                <span><strong>{item.label}</strong><div className="small muted">{item.role}</div></span>
                <strong>{item.key === 'phone_pulse' ? 'główne' : item.key === 'guest_messages' ? 'korelacja' : 'fallback'}</strong>
              </div>
            ))}
          </div>
        </article>
      )}

      <div className="integration-grid">
        <article className={`card integration-card status-${status?.email_import?.tone || 'warning'}`}>
          <div className="integration-card-head">
            <span className="integration-icon">📬</span>
            <div>
              <h2>Email Booking</h2>
              <p className="hint">Awaryjny fallback: używaj tylko gdy telefon / Pulse nie wystarczy.</p>
            </div>
          </div>
          <div className={`status-pill ${status?.email_import?.tone || 'warning'}`}>{status?.email_import?.label || 'Ładuję status...'}</div>
          <div className="channel-list">
            <div className="channel-row"><span>Ostatnio działał</span><strong>{formatDateTime(status?.email_import?.finished_at)}</strong></div>
            <div className="channel-row"><span>Następne sprawdzenie</span><strong>{formatDateTime(status?.email_import?.next_run_at)}</strong></div>
            <div className="channel-row"><span>Maile sprawdzone / import</span><strong>{status?.email_import ? `${status.email_import.checked_count || 0} / ${status.email_import.imported_count || 0}` : '—'}</strong></div>
          </div>
          {status?.email_import?.error && <p className="error">{status.email_import.error}</p>}
          {status?.email_import?.latest_email && <p className="integration-note">Ostatni email: {status.email_import.latest_email.subject}</p>}
          <button type="button" className="secondary integration-run-button" disabled={running === 'email_import'} onClick={() => runNow('email_import')}>
            {running === 'email_import' ? 'Uruchamiam…' : 'Sprawdź teraz'}
          </button>
          <IntegrationLog title="Log Email Booking" items={logs?.email_import || []} />
        </article>

        <article className={`card integration-card status-${status?.guest_messages?.tone || 'warning'}`}>
          <div className="integration-card-head">
            <span className="integration-icon">💬</span>
            <div>
              <h2>Wiadomości gości</h2>
              <p className="hint">Paweł spina wiadomości z rezerwacjami. Źródłem wejściowym jest teraz telefon / Pulse.</p>
            </div>
          </div>
          <div className={`status-pill ${status?.guest_messages?.tone || 'warning'}`}>{status?.guest_messages?.label || 'Ładuję status...'}</div>
          <div className="channel-list">
            <div className="channel-row"><span>Ostatni odczyt</span><strong>{formatDateTime(status?.guest_messages?.finished_at)}</strong></div>
            <div className="channel-row"><span>Następne sprawdzenie</span><strong>{formatDateTime(status?.guest_messages?.next_run_at)}</strong></div>
            <div className="channel-row"><span>Rezerwacje / transkrypty</span><strong>{status?.guest_messages ? `${status.guest_messages.checked_count || 0} / ${status.guest_messages.imported_count || 0}` : '—'}</strong></div>
            <div className="channel-row"><span>Ostatnia wiadomość</span><strong>{formatDateTime(status?.guest_messages?.latest_message?.received_at)}</strong></div>
          </div>
          {status?.guest_messages?.error && <p className="error">{status.guest_messages.error}</p>}
          {statusErr && <p className="error">{statusErr}</p>}
          <button type="button" className="secondary integration-run-button" disabled={running === 'guest_messages'} onClick={() => runNow('guest_messages')}>
            {running === 'guest_messages' ? 'Uruchamiam…' : 'Sprawdź teraz'}
          </button>
          <IntegrationLog title="Log Wiadomości gości" items={logs?.guest_messages || []} />
        </article>

        <article className={`card integration-card status-${status?.whatsapp_group?.tone || 'warning'}`}>
          <div className="integration-card-head">
            <span className="integration-icon">🟢</span>
            <div>
              <h2>WhatsApp: grupa Jana Kazimierza</h2>
              <p className="hint">Czyta grupę operacyjną, zapisuje zdarzenia i aktualizuje gotowość mieszkania. Nie odpisuje automatycznie.</p>
            </div>
          </div>
          <div className={`status-pill ${status?.whatsapp_group?.tone || 'warning'}`}>{status?.whatsapp_group?.label || 'Ładuję status...'}</div>
          <div className="channel-list">
            <div className="channel-row"><span>Ostatnia synchronizacja</span><strong>{formatDateTime(status?.whatsapp_group?.finished_at)}</strong></div>
            <div className="channel-row"><span>Następne sprawdzenie</span><strong>{formatDateTime(status?.whatsapp_group?.next_run_at)}</strong></div>
            <div className="channel-row"><span>Wiadomości / nowe / do weryfikacji</span><strong>{status?.whatsapp_group ? `${status.whatsapp_group.checked_count || 0} / ${status.whatsapp_group.imported_count || 0} / ${status.whatsapp_group.pending_count || 0}` : '—'}</strong></div>
            <div className="channel-row"><span>Ostatnia wiadomość</span><strong>{formatDateTime(status?.whatsapp_group?.latest_event?.recorded_at)}</strong></div>
          </div>
          {status?.whatsapp_group?.latest_event?.body && (
            <p className="integration-note">{status.whatsapp_group.latest_event.sender_name || 'WhatsApp'}: {status.whatsapp_group.latest_event.body}</p>
          )}
          {status?.whatsapp_group?.error && <p className="error">{status.whatsapp_group.error}</p>}
          <button type="button" className="secondary integration-run-button" disabled={running === 'whatsapp_group'} onClick={() => runNow('whatsapp_group')}>
            {running === 'whatsapp_group' ? 'Uruchamiam…' : 'Sprawdź teraz'}
          </button>
          <IntegrationLog title="Log WhatsApp" items={logs?.whatsapp_group || []} />
        </article>

        <article className={`card integration-card integration-card-primary status-${status?.phone_pulse?.tone || 'warning'}`}>
          <div className="integration-card-head">
            <span className="integration-icon">📱</span>
            <div>
              <h2>Telefon / Booking Pulse</h2>
              <p className="hint">Główne źródło informacji: to, co widać na telefonie, trafia do bazy jako pierwszy sygnał rezerwacji i wiadomości.</p>
            </div>
          </div>
          <div className={`status-pill ${status?.phone_pulse?.tone || 'warning'}`}>{status?.phone_pulse?.label || 'Ładuję status...'}</div>
          <div className="channel-list">
            <div className="channel-row"><span>Ostatni test Gabriela</span><strong>{formatDateTime(status?.phone_pulse?.last_run_at)}</strong></div>
            <div className="channel-row"><span>Bateria / temperatura</span><strong>{status?.phone_pulse?.battery ? `${status.phone_pulse.battery.level ?? '—'}% / ${status.phone_pulse.battery.temperature_c ?? '—'}°C` : '—'}</strong></div>
            <div className="channel-row"><span>Ładowanie USB</span><strong>{status?.phone_pulse?.battery?.usb_powered ? 'tak' : 'nie'}</strong></div>
            <div className="channel-row"><span>Pulse zalogowany</span><strong>{status?.phone_pulse?.booking_pulse_looks_logged_in ? 'tak' : 'nie'}</strong></div>
            <div className="channel-row"><span>Odczyt rezerwacji/wiadomości</span><strong>{status?.phone_pulse?.pulse_watch ? formatDateTime(status.phone_pulse.pulse_watch.last_run_at) : 'brak'}</strong></div>
            <div className="channel-row"><span>Widoczne rezerwacje / wiadomości</span><strong>{status?.phone_pulse?.pulse_watch ? `${status.phone_pulse.pulse_watch.reservations_count || 0} / ${status.phone_pulse.pulse_watch.messages_count || 0}` : '—'}</strong></div>
            <div className="channel-row"><span>Rola telefonu</span><strong>{status?.phone_pulse?.pulse_watch?.mode === 'phone_primary_candidate' ? 'główne źródło' : 'główne źródło / health'}</strong></div>
          </div>
          {status?.phone_pulse?.pulse_watch?.messages_preview?.length > 0 && (
            <p className="integration-note">Ostatni odczyt Pulse: {status.phone_pulse.pulse_watch.messages_preview.slice(0, 4).join(' · ')}</p>
          )}
          {status?.phone_pulse?.device && <p className="integration-note">Urządzenie: {status.phone_pulse.device}</p>}
          {status?.phone_pulse?.error && <p className="error">{status.phone_pulse.error}</p>}
        </article>
      </div>

      <div className="integration-grid">
        <article className="card integration-card integration-card-primary">
          <div className="integration-card-head">
            <span className="integration-icon">🤖</span>
            <div>
              <h2>Automatyczny import</h2>
              <p className="hint">Główna ścieżka pracy — bez ręcznego wklejania emaili.</p>
            </div>
          </div>
          <ul className="integration-checklist">
            <li><strong>Telefon / Booking Pulse</strong> — główne źródło rezerwacji i wiadomości dla portalu.</li>
            <li><strong>Hermes → Portal</strong> — gotowe rezerwacje trafiają do endpointu integracyjnego.</li>
            <li><strong>Sprzątanie</strong> — po imporcie portal automatycznie tworzy zadanie dla Marii.</li>
          </ul>
          <div className="integration-endpoint">
            <span>Endpoint techniczny</span>
            <code>POST /api/integrations/hermes/reservations</code>
          </div>
        </article>

        <article className="card integration-card">
          <div className="integration-card-head">
            <span className="integration-icon">📊</span>
            <div>
              <h2>Źródła rezerwacji</h2>
              <p className="hint">Szybka kontrola, czy import faktycznie zasila bazę.</p>
            </div>
          </div>
          {channels.length ? (
            <div className="channel-list">
              {channels.map(([label, count]) => (
                <div className="channel-row" key={label}>
                  <span>{label}</span>
                  <strong>{count}</strong>
                </div>
              ))}
            </div>
          ) : (
            <p className="hint">Brak rezerwacji w bazie.</p>
          )}
          {latestImported && (
            <div className="integration-note">
              Ostatnio zaimportowano: <strong>{latestImported.external_id || `#${latestImported.id}`}</strong>
            </div>
          )}
        </article>
      </div>

      <div className="card manual-import-card">
        <div className="manual-import-header">
          <div>
            <h2>Awaryjny import z emaila</h2>
            <p className="hint">
              Używaj tylko wtedy, gdy trzeba odtworzyć pojedynczą rezerwację, a telefon / Pulse nie wystarczył.
            </p>
          </div>
          <button type="button" className="secondary" onClick={() => setManualOpen(v => !v)}>
            {manualOpen ? 'Ukryj formularz' : 'Pokaż formularz'}
          </button>
        </div>

        {manualOpen && (
          <div className="columns manual-import-body">
            <form onSubmit={submit}>
              <label className="form-label">Treść emaila Booking.com</label>
              <textarea
                value={emailText}
                onChange={e => setEmailText(e.target.value)}
                placeholder="Wklej tutaj pełną treść emaila z Booking.com..."
              />
              {err && <div className="error">{err}</div>}
              <button>Importuj rezerwację</button>
            </form>
            <div className="manual-import-result">
              <h3>Wynik ostatniego importu</h3>
              {!result && <p className="hint">Nie wykonano jeszcze ręcznego importu w tej sesji.</p>}
              {result && (
                <div className="import-result-box">
                  <p><strong>Status:</strong> {result.result?.status}</p>
                  <p><strong>Gość:</strong> {result.parsed?.guest_name}</p>
                  <p><strong>Przyjazd:</strong> {result.parsed?.checkin}</p>
                  <p><strong>Wyjazd:</strong> {result.parsed?.checkout}</p>
                  <p><strong>Źródło:</strong> {channelLabel(result.parsed?.source)}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
